"use client";

import { useState, useCallback, useRef } from "react";
interface UseDealApiMutationOptions<TResult> {
  onSuccess?: (data: TResult) => void;
  onError?: (error: string) => void;
}
interface UseDealApiMutationReturn<TResult> {
  mutate: (url: string, options?: RequestInit) => Promise<TResult | undefined>;
  isSubmitting: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Shared hook for deal-related API mutations.
 *
 * Wraps the common fetch → check response.ok → parse JSON → error handling
 * pattern used across admin deal components.
 */
export function useDealApiMutation<TResult = unknown>(options?: UseDealApiMutationOptions<TResult>): UseDealApiMutationReturn<TResult> {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const reset = useCallback(() => {
    setError(null);
  }, []);
  const mutate = useCallback(async (url: string, fetchOptions?: RequestInit): Promise<TResult | undefined> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch(url, fetchOptions);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || "Fehler beim Aktualisieren");
      }
      const data_0 = await response.json();
      const result = (data_0.data ?? data_0.payment ?? data_0) as TResult;
      optionsRef.current?.onSuccess?.(result);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Fehler beim Aktualisieren";
      setError(message);
      optionsRef.current?.onError?.(message);
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  }, []);
  return {
    mutate,
    isSubmitting,
    error,
    reset
  };
}